// import * as JsStore from "jsstore";
import Dexie from "dexie";
// import { IDataBase, DATA_TYPE, ITable } from "jsstore";
import { v1 as uuidv1 } from "uuid";
import ICreateOrderDTO from "../models/DTO/ICreateOrderDTO";
import ICacheHealth from "../models/ICacheHealth";
import ICatalog from "../models/ICatalog";
import ICatalog_Item from "../models/ICatalog_Item";
import IClient from "../models/IClient";
import IConnection_Token from "../models/IConnection_Token";
import moment from "moment";
import { isNil, find, map, isNumber, get, filter } from "lodash-es";
import IIdbCachedData from "../models/IIdbCachedData";
import IIdbCatalog_Items from "../models/IIdbCatalog_Items";
import IIdbCatalogs from "../models/IIdbCatalogs";
import IIdbClients from "../models/IIdbClients";
import IIdbFull_Text_Catalog_Items from "../models/IIdbFull_Text_Catalog_Items";
import IIdbGeneral from "../models/IIdbGeneral";
import IIdbOrder_Items from "../models/IIdbOrder_Items";
import IIdbOrders from "../models/IIdbOrders";
import IIdbTokens from "../models/IIdbTokens";
import IIdbVendors from "../models/IIdbVendors";

import IOfflineCreateOrderItem from "../models/IOfflineCreateOrderItem";
import IOfflineOrder from "../models/IOfflineOrder";
import IVendorModel from "../models/IVendorModel";
import SupplierService from "./SupplierService";
import UserService from "./UserService";
import VendorService from "./VendorService";

export default class IdbService extends Dexie {
    CachedData: Dexie.Table<IIdbCachedData, number>;
    Orders: Dexie.Table<IIdbOrders, number>;
    Order_Items: Dexie.Table<IIdbOrder_Items, number>;
    Vendors: Dexie.Table<IIdbVendors, number>;
    Clients: Dexie.Table<IIdbClients, number>;
    Catalog: Dexie.Table<IIdbCatalogs, number>;
    Catalog_Items: Dexie.Table<IIdbCatalog_Items, number>;
    Full_Text_Catalog_Items: Dexie.Table<IIdbFull_Text_Catalog_Items, number>;
    General: Dexie.Table<IIdbGeneral, number>;
    Token: Dexie.Table<IIdbTokens, number>;
    // export const idbCon = new Dexie(dbname);
    // const dataBase = getDatabaseStores();
    // idbCon.version(1).stores(dataBase);

    constructor() {
        super("Orderwerks");

        const databaseStores = this.getDatabaseStores();
        this.version(2).stores(databaseStores);

        this.CachedData = this.table("CachedData");
        this.Orders = this.table("Orders");
        this.Order_Items = this.table("Order_Items");
        this.Vendors = this.table("Vendors");
        this.Clients = this.table("Clients");
        this.Catalog = this.table("Catalog");
        this.Catalog_Items = this.table("Catalog_Items");
        this.General = this.table("General");
        this.Token = this.table("Token");
        this.Full_Text_Catalog_Items = this.table("Full_Text_Catalog_Items");
    }

    deleteDatabase = async () => {
        await Dexie.delete("Orderwerks");
    };

    createDatabase = () => {
        const db = new Dexie("Orderwerks");
        const databaseStores = this.getDatabaseStores();
        db.version(2).stores(databaseStores);
        db.open();
    };

    getDatabaseStores = () => {
        const stores = {
            CachedData: "++id, name, data",
            Orders: "++id, guid, isSaved, reconcileData, apiData, vendorGuid, createdAt, updatedAt, deletedAt",
            Order_Items: "++id, guid, orderId, catalogItemId, qty, listPriceFrozen, isSaved, apiData, vendorGuid, createdAt, updatedAt",
            Vendors: "++id, guid, name, isSaved, isNew, apiData, vendorGuid, createdAt, updatedAt",
            Clients: "++id, guid, name, isSaved, isNew, apiData, createdAt, updatedAt",
            Catalog: "++id, guid, name, totalCatalogItems, supplierGuid, apiData, createdAt, updatedAt",
            Catalog_Items: "++id, guid, catalogId, name, upc, apiData, createdAt, updatedAt",
            Full_Text_Catalog_Items: "++id, catalogId, catalogItemId, fullTextName, createdAt, updatedAt",
            General: "++id, userApiData, supplierApiData, vendorApiData, createdAt, updatedAt",
            Token: "++id, apiCall, apiData, createdAt, updatedAt",
        };

        return stores;
    };

    saveLoginData = async (user: any, vendor: any, supplier: any) => {
        await this.General.clear();

        await this.General.add({
            userApiData: user,
            supplierApiData: supplier,
            vendorApiData: vendor,
            createdAt: new Date(),
            updatedAt: new Date(),
        });

        return;
    };

    clearCatalogsAndCatalogItems = async (): Promise<void> => {
        await this.Catalog.clear();
        await this.Catalog_Items.clear();
        await this.Full_Text_Catalog_Items.clear();
        return;
    };

    saveCatalogData = async (catalogData: ICatalog[], supplierGuid: string): Promise<void> => {
        for (const catalog of catalogData) {
            await this.Catalog.put({
                guid: uuidv1(),
                apiData: catalog,
                supplierGuid,
                name: catalog.name,
                totalCatalogItems: 0,
                createdAt: new Date(),
                updatedAt: new Date(),
            });
        }

        return;
    };

    saveCatalogItemsData = async (
        catalog: ICatalog,
        catalogItemDataArr: ICatalog_Item[],
        initialData = false,
        totalCatalogItems: number
    ): Promise<void> => {
        const catalogResults: any = await this.Catalog.where("id").above(0).toArray();

        let currentCatalogRow: any;

        if (catalogResults.length === 1) {
            currentCatalogRow = catalogResults[0];
        } else {
            for (const catalogRow of catalogResults) {
                if (catalog.name === catalogRow.name) {
                    currentCatalogRow = catalogRow;
                }
            }
        }

        if (isNil(currentCatalogRow)) {
            return;
        } else {
            // console.log("currentCatalogRow", currentCatalogRow);

            const catalogId = currentCatalogRow.id; // this is the LOCAL id value.

            if (!catalogId) {
                // console.log("No catalogId for current catalog row: ", currentCatalogRow);
                return;
            }

            if (initialData) {
                const rowsDeleted = await this.table("Catalog").delete(catalogId);
                const itemRowsDeleted = await this.table("Catalog_Items").where("catalogId").equals(catalogId).delete();
                const fullTextRowsDeleted = await this.table("Full_Text_Catalog_Items").where("catalogId").equals(catalogId).delete();
            }

            for (const catalogItemData of catalogItemDataArr) {
                const dashSplit = catalogItemData.name.split(/[-.]/);

                let secondDashJoin: string | undefined;

                const fullTextName: string[] = [...dashSplit, catalogItemData.name];

                if (dashSplit.length === 3) {
                    secondDashJoin = dashSplit[1] + "-" + dashSplit[2];

                    fullTextName.push(secondDashJoin);
                }

                const catalogItemId = await this.Catalog_Items.put({
                    apiData: catalogItemData,
                    name: catalogItemData.name,
                    upc: catalogItemData.upc,
                    catalogId,
                    guid: catalogItemData.guid,
                    createdAt: new Date(),
                    updatedAt: new Date(),
                });

                for (const name of fullTextName) {
                    await this.Full_Text_Catalog_Items.put({
                        catalogId,
                        catalogItemId,
                        fullTextName: name,
                        createdAt: new Date(),
                        updatedAt: new Date(),
                    });
                }
            }

            // console.log("totalCatalogItems: ", totalCatalogItems, currentCatalogRow.totalCatalogItems);

            if (initialData && currentCatalogRow.totalCatalogItems !== totalCatalogItems) {
                await this.Catalog.put({ ...currentCatalogRow, totalCatalogItems, updatedAt: new Date() });
            }

            return;
        }
    };

    clearCache = async (type: string): Promise<void> => {
        switch (type) {
            case "client":
                await this.Clients.clear();
                break;

            case "vendor":
                await this.Vendors.clear();
                break;

            case "catalog":
                await this.Catalog.clear();
                await this.Catalog_Items.clear();
                await this.Full_Text_Catalog_Items.clear();
                break;
        }

        return;
    };

    saveClientData = async (clients: IClient[]): Promise<void> => {
        await this.Clients.clear();

        for (const client of clients) {
            await this.Clients.put({
                apiData: client,
                name: client.name,
                guid: uuidv1(),
                createdAt: new Date(),
                updatedAt: new Date(),
                isNew: 1,
                isSaved: 0,
            });
        }

        return;
    };

    saveVendors = async (supplierVendorTokens: IConnection_Token[], deleteAll: boolean) => {
        if (deleteAll) {
            await this.Vendors.where("isNew").equals(0).delete();
        }

        for (const token of supplierVendorTokens) {
            await this.Vendors.put({
                apiData: token,
                isNew: 0,
                name: token.Vendor!.name,
                guid: token.Vendor!.guid,
                createdAt: new Date(),
                updatedAt: new Date(),
                vendorGuid: "",
                isSaved: 1,
            });
        }

        return;
    };

    /**
     * Store the order and generate with an 'offline' guid.
     * @param payload
     */
    createOrder = async (payload: ICreateOrderDTO): Promise<string> => {
        const offlineGuid = `offline-${uuidv1()}`;
        await this.Orders.put({
            apiData: payload,
            guid: offlineGuid,
            createdAt: new Date(),
            updatedAt: new Date(),
            isSaved: 0,
            reconcileData: null,
            vendorGuid: "",
        });
        return offlineGuid;
    };

    /**
     * Store the order and generate with an 'offline' guid.
     * @param payload
     */
    markOrderSaved = async (offlineGuid: string, isSaved: boolean): Promise<boolean> => {
        await this.Orders.where("guid")
            .equals(offlineGuid)
            .modify({ isSaved: isSaved ? 1 : 0 });

        return true;
    };

    createOrderItem = async (payload: IOfflineCreateOrderItem): Promise<void> => {
        /**

             Catalog_Item: {id: 4, guid: "582da86c-46a1-436f-94a7-5657bbb428ca", catalog_id: 1, category_id: null,…}
             catalog_item_id: 4
             cost_price_frozen: "200"
             created_at: "2019-12-08T19:47:42.000Z"
             description: null
             guid: "b8110b3e-345e-414f-9773-e89ba279997a"
             id: 7973
             is_manual_price: false
             item_no: "1"
             list_price_frozen: "200"
             note: null
             order_id: 976
             parent_order_item_id: null
             qty: "1"
             updated_at: "2019-12-08T19:47:42.000Z"
             */

        //@ts-expect-error
        payload.guid = uuidv1();

        //@ts-expect-error
        await this.Order_Items.put(payload);

        return;
    };

    renumberOrderItems = async (payload: any): Promise<void> => {
        // const renumberArr = payload.renumberArr; // renumberArr.push({ order_item_guid: item.guid, item_no: currentNumber })
        //
        // for (const renumberItem of renumberArr) {
        //     await this.Order_Items.where("guid").equals(renumberItem.order_item_guid).modify({
        //         item_no: renumberItem.item_no,
        //     });
        // }

        return;
    };

    updateOrderItem = async (orderItem: any): Promise<void> => {
        await this.Order_Items.update(orderItem.id, {
            qty: orderItem.qty,
        });

        return;
    };

    loadGeneralData = async (): Promise<any> => {
        const generalData: any = await this.General.where("id").above(0).first();

        const firstRow = generalData;

        if (firstRow) {
            const { userApiData, vendorApiData, supplierApiData } = firstRow;

            UserService.saveUserData(userApiData);
            VendorService.saveVendorData(vendorApiData);
            SupplierService._saveSupplierData(firstRow.supplierApiData);

            return { userModel: userApiData, vendorModel: vendorApiData, supplierModel: supplierApiData };
        } else {
            return { userModel: undefined, vendorModel: undefined, supplierModel: undefined };
        }
    };

    findVendorsByName = async (name: string): Promise<IVendorModel[]> => {
        const foundVendors = await this.Vendors.filter((vendor: IIdbVendors) => {
            const regex = new RegExp(name, "gi");
            return regex.test(vendor.name);
        }).toArray();

        const parsedVendors = map(foundVendors, (vendor: any) => {
            return vendor.apiData.Vendor;
        });

        return parsedVendors;
    };

    findCatalogItemsByString = async (
        catalog: ICatalog,
        typeahead: string,
        limit: number = 20,
        offset: number = 0
    ): Promise<ICatalog_Item[]> => {
        // console.log("findCatalogItemsByString", catalog);
        const foundCatalogs: any[] = await this.Catalog.where("id").above(0).toArray();

        const catalogFound = find(foundCatalogs, (storedCatalog) => {
            return catalog.guid === storedCatalog.apiData.guid;
        });

        // console.log("catalogFound: ", catalogFound);
        //
        // let foundCatalogItems = await this.Catalog_Items.where("[fullTextName]")
        //     .anyOfIgnoreCase([typeahead])
        //     .and((catalogItem) => { return Number(catalogItem.catalogId) === Number(catalogFound.id) })
        //     .toArray();

        // let foundCatalogItems = await this.Catalog_Items.where("catalogId")
        //     .equals(catalogFound.id)
        //     // .and()
        //     // .filter((catalogItem) => {
        //     //     const regex = new RegExp(typeahead, "gi");
        //     //     return regex.test(catalogItem.name);
        //     // })
        //     .toArray();
        // let foundCatalogItems = await this.Catalog_Items.where("catalogId")
        //     .equals(catalogFound.id)
        //     .filter((catalogItem) => {
        //         return RegExp(typeahead, "gi").test(catalogItem.name);
        //     })
        //     .toArray();
        //
        // const matchedItems = foundCatalogItems.filter((item) => {
        //     return item.name.indexOf(typeahead) !== -1;
        // });

        // let foundFullTextMatches = await this.Full_Text_Catalog_Items
        //     .filter((fullTextItem) => {
        //         if (Number(fullTextItem.catalogId) === Number(catalogFound.id)) {
        //             return RegExp(typeahead, "gi").test(fullTextItem.fullTextName);
        //         } else {
        //             return false;
        //         }
        //     })
        //     .toArray();
        console.log("limit, offset", limit, offset);
        let calculatedLimit: number | undefined;

        if (typeahead.length < 2) {
            calculatedLimit = limit;
        }

        let foundFullTextMatches: IIdbFull_Text_Catalog_Items[] = [];

        // we need to prevent a huge number of items from returning for if less than 2 chars.
        if (typeahead.length < 2) {
            foundFullTextMatches = await this.Full_Text_Catalog_Items.where("fullTextName")
                .startsWithIgnoreCase(typeahead)
                .filter((fullTextItem) => {
                    return Number(fullTextItem.catalogId) === Number(catalogFound.id);
                })
                .limit(limit)
                // .offset(offset)
                .toArray();

            const foundCatalogItemIds = foundFullTextMatches.map((match) => {
                return match.catalogItemId;
            });

            // console.log("foundCatalogItems: ", foundCatalogItems);
            const foundCatalogItems = await this.Catalog_Items.where("id").anyOf(foundCatalogItemIds).toArray();

            const parsedCatalogItems = map(foundCatalogItems, (catalogItem) => {
                return catalogItem.apiData;
            });

            return parsedCatalogItems;
        } else {
            if (typeahead.length === 12 && isNumber(Number(typeahead))) {
                const foundCatalogItems = await this.Catalog_Items.where("upc").equals(typeahead).toArray();

                const parsedCatalogItems = map(foundCatalogItems, (catalogItem) => {
                    return catalogItem.apiData;
                });

                return parsedCatalogItems;
            } else {
                foundFullTextMatches = await this.Full_Text_Catalog_Items.where("fullTextName")
                    .startsWithIgnoreCase(typeahead)
                    .filter((fullTextItem) => {
                        return Number(fullTextItem.catalogId) === Number(catalogFound.id);
                    })
                    // .offset(offset)
                    .toArray();
            }

            const foundCatalogItemIds = foundFullTextMatches.map((match) => {
                return match.catalogItemId;
            });

            // console.log("foundCatalogItems: ", foundCatalogItems);
            const foundCatalogItems = await this.Catalog_Items.where("id").anyOf(foundCatalogItemIds).toArray();

            const parsedCatalogItems = map(foundCatalogItems, (catalogItem) => {
                return catalogItem.apiData;
            });

            return parsedCatalogItems;
        }
    };

    getCatalogs = async (): Promise<ICatalog[]> => {
        const foundCatalogs = await this.Catalog.where("id").above(0).toArray();

        const parsedCatalogs = map(foundCatalogs, (catalog: any) => {
            return catalog.apiData;
        });

        return parsedCatalogs;
    };

    getOfflineVendors = async (): Promise<any[]> => {
        const foundVendors: any[] = await this.Vendors.where("isNew").equals(1).toArray();

        const filteredVendors = filter(foundVendors, (vendor: any) => {
            return vendor;
        });

        const parsedVendors = map(filteredVendors, (vendor: any) => {
            const data = { ...vendor.apiData };
            data.offlineId = vendor.id;
            data.offlineGuid = vendor.guid;

            return data;
        });

        return parsedVendors || [];
    };

    getOrders = async (includeOrderItems = false): Promise<IOfflineOrder[]> => {
        const foundOrders: any[] = await this.Orders.where("id").above(0).toArray();

        const filteredOrders = filter(foundOrders, (order: any) => {
            if (!order.deletedAt) {
                return order;
            }
        });

        const deletedOrders = filter(foundOrders, (order: any) => {
            if (order.deletedAt) {
                return order;
            }
        });

        const parsedOrders = map(filteredOrders, (order: any) => {
            const data = { ...order.apiData };
            data.offlineId = order.id;
            data.offlineGuid = order.guid;
            data.isSaved = order.isSaved;
            data.createdAt = order.createdAt;
            data.updatedAt = order.updatedAt;

            return data;
        });

        if (includeOrderItems) {
            for (const order of parsedOrders) {
                const Order_Items = await this.Order_Items.where("orderId").equals(order.offlineId).toArray();

                order.Order_Items = Order_Items;
                order.items = Order_Items;
            }
        }

        // Prune all Deleted Orders after 1 week
        if (deletedOrders) {
            for (const deletedOrder of deletedOrders) {
                if (moment(deletedOrder.deletedAt).diff(moment(), "days") >= 7) {
                    await this.Orders.where("id").equals(deletedOrder.id).delete();
                }
            }
        }

        return parsedOrders || [];
    };

    updateOrderReconcileData = async (offlineGuid: string, data: any[]): Promise<void> => {
        await this.Orders.where("guid").equals(offlineGuid).modify({ reconcileData: data });

        return;
    };

    getOrderByLocalGuid = async (guid: string): Promise<any> => {
        const foundOrder: any = await this.Orders.where("guid").equals(guid).first();

        const Order_Items = await this.Order_Items.where("orderId").equals(foundOrder.id).toArray();

        // console.log("foundOrder.apiData.vendorGuid", foundOrder);

        const OrderVendor: any[] = await this.Vendors.where("guid").equals(foundOrder.apiData.vendorGuid).toArray();

        // console.log("OrderVendor", OrderVendor);

        const compiledOrder: any = {
            //@ts-expect-error
            ...foundOrder.apiData,
            //@ts-expect-error
            id: foundOrder.id,
            //@ts-expect-error
            offlineGuid: foundOrder.guid,
            Order_Items,
            items: Order_Items,
            attachments: [],
            details: foundOrder.apiData.details,
            supplierVendorToken: OrderVendor[0].apiData,
            reconcileData: foundOrder.reconcileData,
        };

        return compiledOrder;
    };

    updateOrderDetails = async (guid: string, details: any): Promise<boolean> => {
        const foundOrder = await this.Orders.where("guid").equals(guid).first();

        if (foundOrder) {
            await this.Orders.update(foundOrder.id!, {
                apiData: {
                    ...details,
                },
            });
        }

        return true;
    };

    deleteOrderByLocalGuid = async (guid: string): Promise<void> => {
        await this.Orders.where("guid").equals(guid).modify({ deletedAt: new Date() });

        return;
    };

    deleteOrderItem = async (rowData: any): Promise<void> => {
        console.log("rowData", rowData);
        const rowsDeleted = await this.Order_Items.where("guid").equals(rowData.orderEntryItemGuid).delete();

        console.log("rowsDeleted: ", rowsDeleted);

        return;
    };

    getCacheHealth = async (): Promise<ICacheHealth> => {
        const generalData: IIdbGeneral | undefined = await this.General.where("id").above(0).first();

        const catalogsData: IIdbCatalogs[] = await this.Catalog.where("id").above(0).toArray();

        const vendorsData: IIdbVendors[] = await this.Vendors.where("id").above(0).toArray();

        const clientsData: IIdbClients[] = await this.Clients.where("id").above(0).toArray();

        const lastGeneralUpdate: any = generalData ? moment(get(generalData, "[0].updatedAt")) : undefined;
        const lastCatalogsUpdate: any = catalogsData.length ? moment(get(catalogsData, "[0].updatedAt")) : undefined;
        const lastVendorsUpdate: any = vendorsData.length ? moment(get(vendorsData, "[0].updatedAt")) : undefined;
        const lastClientsUpdate: any = clientsData.length ? moment(get(clientsData, "[0].updatedAt")) : undefined;

        const totalClients = clientsData.length;
        const totalVendors = vendorsData.length;
        const totalCatalogs = catalogsData.length;

        let isOfflineReady = false;

        const momentsArr: any[] = [];

        if (lastGeneralUpdate) {
            momentsArr.push(lastGeneralUpdate);
        }

        if (lastCatalogsUpdate) {
            momentsArr.push(lastCatalogsUpdate);
        }

        if (lastVendorsUpdate) {
            momentsArr.push(lastVendorsUpdate);
        }

        if (lastClientsUpdate) {
            momentsArr.push(lastClientsUpdate);
        }

        const REFERENCE = moment.min(momentsArr); // fixed just for testing, use moment();
        // var TODAY = REFERENCE.clone().startOf("day");
        // var YESTERDAY = REFERENCE.clone()
        //     .subtract(1, "days")
        //     .startOf("day");
        const A_WEEK_OLD = REFERENCE.clone().subtract(7, "days").startOf("day");

        // const isToday = (momentDate: moment.Moment) => {
        //     return momentDate.isSame(TODAY, "d");
        // };
        //
        // const isYesterday = (momentDate: moment.Moment) => {
        //     return momentDate.isSame(YESTERDAY, "d");
        // };

        const isWithinAWeek = (momentDate: moment.Moment) => {
            return momentDate.isAfter(A_WEEK_OLD);
        };

        // const isTwoWeeksOrMore = (momentDate: moment.Moment) => {
        //     return !isWithinAWeek(momentDate);
        // };

        if (isWithinAWeek(REFERENCE)) {
            isOfflineReady = true;

            if (!totalVendors) {
                isOfflineReady = false;
            }

            if (!totalCatalogs) {
                isOfflineReady = false;
            }
        }

        const payload: ICacheHealth = {
            lastGeneralUpdate,
            lastCatalogsUpdate,
            lastVendorsUpdate,
            lastClientsUpdate,
            totalClients,
            totalVendors,
            totalCatalogs,
            isOfflineReady,
            generalData,
            catalogsData,
            vendorsData,
            clientsData,
        };

        return payload;
    };
}
